/**
 * useAccountSettingsHelpers Hook
 * 
 * Provides helper functions for account settings operations.
 */

import { useMemo } from "react";
import { Account, Currency, AccountGroupDetailField } from "../../../types";
import { AccountGroup } from "./useAccountSettingsUIState";

export interface UseAccountSettingsHelpersParams {
  accounts: Account[];
  groups: AccountGroup[];
  currencies: Currency[];
  defaultCurrency: string;
  editingGroupName: string;
  accountFormData: {
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: Record<string, string | number>;
  };
  editingAccount: Account | null;
  initialAccountFormData: {
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: Record<string, string | number>;
  } | null;
  showAccountDetails: boolean;
  initialShowAccountDetails: boolean;
}

export interface UseAccountSettingsHelpersReturn {
  // Group helpers
  isGroupInUse: (groupName: string) => boolean;
  getAccountsUsingGroup: (groupName: string) => Account[];
  
  // Currency helpers
  formatCurrency: (amount: number, currency?: string) => string;
  getCurrencyRate: (currencyCode: string) => number;
  
  // Account grouping
  activeAccounts: Account[];
  archivedAccounts: Account[];
  groupedAccounts: Record<string, Account[]>;
  groupedArchivedAccounts: Record<string, Account[]>;
  getArchivedGroupBalance: (groupName: string) => number;
  
  // Account detail helpers
  getAccountDetailsForDisplay: (account: Account) => Array<{ name: string; value: string | number }>;
  getSelectedGroupDetails: () => AccountGroupDetailField[];
  hasAccountFormChanged: () => boolean;
  
  // Detail validation
  isDetailInUse: (detailName: string) => boolean;
  isListItemInUse: (listItem: string, detailName: string) => boolean;
  
  // Utility helpers
  handleCopyToClipboard: (text: string) => Promise<void>;
}

/**
 * Hook for account settings helper functions
 */
export const useAccountSettingsHelpers = (params: UseAccountSettingsHelpersParams): UseAccountSettingsHelpersReturn => {
  const {
    accounts,
    groups,
    currencies,
    defaultCurrency,
    editingGroupName,
    accountFormData,
    editingAccount,
    initialAccountFormData,
    showAccountDetails,
    initialShowAccountDetails,
  } = params;

  // Check if group is used by any account
  const isGroupInUse = (groupName: string): boolean => {
    return accounts.some((acc) => acc.group === groupName);
  };

  // Get accounts using a group
  const getAccountsUsingGroup = (groupName: string): Account[] => {
    return accounts.filter((acc) => acc.group === groupName);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "USD"): string => {
    // Round to 4 decimal places
    const rounded = Math.round(amount * 10000) / 10000;
    // Format using formatToParts to get control over each part
    const parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    }).formatToParts(rounded);
    
    // Find the fraction part and check if it has significant digits
    const fractionPart = parts.find(p => p.type === "fraction");
    const hasSignificantFraction = fractionPart && fractionPart.value.replace(/0+$/, "") !== "";
    
    let result = "";
    for (const part of parts) {
      if (part.type === "currency") {
        result += part.value;
      } else if (part.type === "integer" || part.type === "group") {
        result += part.value;
      } else if (part.type === "decimal") {
        // Only add decimal separator if there are significant fraction digits
        if (hasSignificantFraction) {
          result += part.value;
        }
      } else if (part.type === "fraction") {
        // Remove trailing zeros from fraction part
        const fraction = part.value.replace(/0+$/, "");
        if (fraction !== "") {
          result += fraction;
        }
      }
    }
    return result;
  };

  // Get currency rate for conversion
  const getCurrencyRate = (currencyCode: string): number => {
    if (currencyCode === defaultCurrency) return 1;
    const currency = currencies.find((c) => c.code === currencyCode);
    if (!currency || currency.rate === null) return 1;
    
    // Get rate for default currency from the currency's rates object
    const rateForDefault = currency.rates?.[defaultCurrency];
    if (rateForDefault !== null && rateForDefault !== undefined) {
      return rateForDefault;
    }
    
    // Fallback to currency.rate if rates object doesn't have default currency
    return currency.rate;
  };

  // Separate active and archived accounts
  const activeAccounts: Account[] = useMemo(() => {
    return accounts.filter((acc) => !acc.isArchived);
  }, [accounts]);

  const archivedAccounts: Account[] = useMemo(() => {
    return accounts.filter((acc) => acc.isArchived === true);
  }, [accounts]);

  // Group accounts by group and sort by order
  const groupedAccounts: Record<string, Account[]> = useMemo(() => {
    return groups.reduce((acc, group) => {
      const groupAccounts = activeAccounts.filter((account) => account.group === group.name);
      // Sort by order field (default to 0 if not present)
      groupAccounts.sort((a, b) => {
        const orderA = (a as any).order ?? 0;
        const orderB = (b as any).order ?? 0;
        return orderA - orderB;
      });
      acc[group.name] = groupAccounts;
      return acc;
    }, {} as Record<string, Account[]>);
  }, [groups, activeAccounts]);

  // Group archived accounts by group
  const groupedArchivedAccounts: Record<string, Account[]> = useMemo(() => {
    return groups.reduce((acc, group) => {
      const groupAccounts = archivedAccounts.filter((account) => account.group === group.name);
      // Sort by order field (default to 0 if not present)
      groupAccounts.sort((a, b) => {
        const orderA = (a as any).order ?? 0;
        const orderB = (b as any).order ?? 0;
        return orderA - orderB;
      });
      acc[group.name] = groupAccounts;
      return acc;
    }, {} as Record<string, Account[]>);
  }, [groups, archivedAccounts]);

  // Calculate balance for a specific group (archived accounts)
  const getArchivedGroupBalance = (groupName: string): number => {
    const groupAccounts = groupedArchivedAccounts[groupName] || [];
    let balance = 0;

    groupAccounts.forEach((acc) => {
      const accountAmount = parseFloat(String(acc.amount || 0));
      
      // Convert account amount to default currency
      let amountInDefaultCurrency = accountAmount;
      
      if (acc.currency !== defaultCurrency) {
        const accountCurrencyRate = getCurrencyRate(acc.currency);
        amountInDefaultCurrency = accountAmount / accountCurrencyRate;
      }
      
      balance += amountInDefaultCurrency;
    });

    return balance;
  };

  // Get account details for display
  const getAccountDetailsForDisplay = (account: Account): Array<{ name: string; value: string | number }> => {
    // If account has details field (even if empty), show all detail fields from group
    if (account.details === undefined) return [];
    
    const group = groups.find((g) => g.name === account.group);
    if (!group?.details) return [];
    
    // Return all detail fields from group, showing "-" for empty values
    return group.details.map((detail) => {
      const value = account.details?.[detail.name];
      return {
        name: detail.name,
        value: value !== undefined && value !== "" ? value : "-",
      };
    });
  };

  // Get details for selected group
  const getSelectedGroupDetails = (): AccountGroupDetailField[] => {
    if (!accountFormData.group) return [];
    const group = groups.find((g) => g.name === accountFormData.group);
    return group?.details || [];
  };

  // Check if form has changed from initial values
  const hasAccountFormChanged = (): boolean => {
    if (!editingAccount || !initialAccountFormData) return true; // Always allow for new accounts
    
    // Compare all fields
    const currentAmount = accountFormData.amount === "" ? "0" : accountFormData.amount;
    const initialAmount = initialAccountFormData.amount === "" ? "0" : initialAccountFormData.amount;
    
    return (
      accountFormData.accountName.trim() !== initialAccountFormData.accountName.trim() ||
      accountFormData.group !== initialAccountFormData.group ||
      currentAmount !== initialAmount ||
      accountFormData.description !== initialAccountFormData.description ||
      accountFormData.currency !== initialAccountFormData.currency ||
      JSON.stringify(accountFormData.details || {}) !== JSON.stringify(initialAccountFormData.details || {}) ||
      showAccountDetails !== initialShowAccountDetails
    );
  };

  // Check if a detail is used by any account
  const isDetailInUse = (detailName: string): boolean => {
    if (!editingGroupName) return false;
    
    // Check if any account in this group has a value for this detail
    return accounts.some((account) => {
      if (account.group !== editingGroupName) return false;
      if (!account.details) return false;
      // Check if the account has this detail name in its details
      return detailName in account.details && account.details[detailName] !== undefined && account.details[detailName] !== "";
    });
  };

  // Check if a list item is used in any account for the detail being edited
  const isListItemInUse = (listItem: string, detailName: string): boolean => {
    if (!editingGroupName) return false;
    
    // Check if any account in this group has this list item as the value for this detail
    return accounts.some((account) => {
      if (account.group !== editingGroupName) return false;
      if (!account.details) return false;
      // Check if the account has this detail and the value matches the list item
      const detailValue = account.details[detailName];
      return detailValue !== undefined && String(detailValue) === listItem;
    });
  };

  // Copy to clipboard
  const handleCopyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  return {
    // Group helpers
    isGroupInUse,
    getAccountsUsingGroup,
    
    // Currency helpers
    formatCurrency,
    getCurrencyRate,
    
    // Account grouping
    activeAccounts,
    archivedAccounts,
    groupedAccounts,
    groupedArchivedAccounts,
    getArchivedGroupBalance,
    
    // Account detail helpers
    getAccountDetailsForDisplay,
    getSelectedGroupDetails,
    hasAccountFormChanged,
    
    // Detail validation
    isDetailInUse,
    isListItemInUse,
    
    // Utility helpers
    handleCopyToClipboard,
  };
};
